import React, { useContext, useState, useEffect } from 'react';
import { AppBar, Toolbar, Box, Button, IconButton, useMediaQuery, Menu, MenuItem, Typography, Select, FormControl, Tabs, Tab } from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import { Dashboard, People, DirectionsBike, Inventory, Receipt, Settings, Menu as MenuIcon, ShoppingCart, Logout, Group, Business, AccountBalance, TwoWheeler, PedalBike, Build } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import api, { setAuthToken } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import { ROLES, hasPermission, SECTIONS, hasSectionAccess, canViewAccountingMovements } from '../../utils/roles';
import logger from '../../utils/logger';

const Navbar = ({ open, toggleSidebar }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
  const [anchorEl, setAnchorEl] = React.useState(null);
  const { user, selectedCompany, updateSelectedCompany } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [companyName, setCompanyName] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  
  // Determina i menu items in base ai permessi dell'utente
  const getMenuItems = () => {
    const items = [
      { text: 'Dashboard', icon: <Dashboard />, path: '/dashboard', permission: null },
    ];

    // Clienti - accessibile a tutti tranne Manager
    if (user?.role !== ROLES.MANAGER) {
      items.push({ text: 'Clienti', icon: <People />, path: '/dashboard/customers', permission: 'ACCESS_CUSTOMERS' });
    }

    // Magazzino - accessibile a tutti
    if (hasPermission(user?.role, 'ACCESS_INVENTORY')) {
      items.push({ text: 'Magazzino', icon: <Inventory />, path: '/dashboard/inventory', permission: 'ACCESS_INVENTORY' });
    }

    // Noleggi - basato sui nuovi permessi granulari
    if (hasSectionAccess(user, SECTIONS.RENTALS)) {
      items.push({ text: 'Noleggi', icon: <PedalBike />, path: '/dashboard/rental', permission: null });
    }

    // Manutenzioni - basato sui nuovi permessi granulari
    if (hasSectionAccess(user, SECTIONS.MAINTENANCE)) {
      items.push({ text: 'Manutenzioni', icon: <Build />, path: '/dashboard/maintenance', permission: 'ACCESS_MAINTENANCE' });
    }

    // Vendite - basato sui nuovi permessi granulari
    if (hasSectionAccess(user, SECTIONS.SALES)) {
      items.push({ text: 'Vendite', icon: <Receipt />, path: '/dashboard/sales', permission: 'ACCESS_SALES' });
    }

    // Shop - basato sui nuovi permessi granulari
    if (hasSectionAccess(user, SECTIONS.SHOP)) {
      items.push({ text: 'Shop', icon: <ShoppingCart />, path: '/dashboard/shop', permission: 'ACCESS_SHOP' });
    }

    // Contabilità: modulo abilitato oppure admin+ (per movimenti contabili)
    if (hasSectionAccess(user, SECTIONS.ACCOUNTING) || canViewAccountingMovements(user)) {
      items.push({ text: 'Contabilità', icon: <AccountBalance />, path: '/dashboard/accounting', permission: 'ACCESS_SETTINGS' });
    }

    // Utenti - accessibile solo a SuperSaiyan e SuperAdmin
    if (hasPermission(user?.role, 'MANAGE_USERS')) {
      items.push({ text: 'Utenti', icon: <Group />, path: '/dashboard/users', permission: 'MANAGE_USERS' });
    }

    // Impostazioni - accessibile solo a SuperSaiyan, SuperAdmin e Admin
    if (hasPermission(user?.role, 'ACCESS_SETTINGS')) {
      items.push({ text: 'Impostazioni', icon: <Settings />, path: '/dashboard/settings', permission: 'ACCESS_SETTINGS' });
    }

    return items;
  };
  
  // 🔒 SICUREZZA: Debug solo in development
  useEffect(() => {
    logger.debug('🔍 DEBUG NAVBAR - User presente:', !!user);
    logger.debug('🔍 DEBUG NAVBAR - Company ID:', user?.companyId);
  }, [user]);

  // Sincronizza il tab attivo con l'URL corrente
  useEffect(() => {
    const currentPath = window.location.pathname;
    const menuItems = getMenuItems();
    const currentIndex = menuItems.findIndex(item => item.path === currentPath);
    if (currentIndex !== -1) {
      setActiveTab(currentIndex);
    }
  }, [window.location.pathname, user]);
  
  // Carica il nome dell'azienda
  useEffect(() => {
    const loadCompanyName = async () => {
      if (selectedCompany) {
        try {
          logger.debug('🔍 DEBUG NAVBAR - Caricamento nome azienda per ID:', selectedCompany);
          const response = await api.get(`/companies/${selectedCompany}`);
          if (response.data && response.data.name) {
            setCompanyName(response.data.name);
            logger.debug('✅ Nome azienda impostato:', response.data.name);
          }
        } catch (err) {
          logger.error('Errore nel caricamento del nome azienda:', err);
          setCompanyName(''); // Reset in caso di errore
        }
      } else {
        setCompanyName(''); // Reset se non c'è selectedCompany
      }
    };
    
    // Carica sempre il nome dell'azienda selezionata
    if (selectedCompany) {
      loadCompanyName();
    }
  }, [selectedCompany]);
  
  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const response = await api.get('/companies');
        setCompanies(response.data);
      } catch (err) {
        logger.error('Errore nel caricamento delle aziende:', err);
      }
    };

    if (user?.role === ROLES.SUPERSAIYAN) {
      fetchCompanies();
    }
  }, [user]);

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };
  
  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleCompanyChange = (event) => {
    const companyId = event.target.value;
    updateSelectedCompany(companyId);
  };

  const handleLogout = () => {
    try {
      setAuthToken(null);
      navigate('/login');
    } catch (err) {
      logger.error('Errore durante il logout:', err);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    const menuItems = getMenuItems();
    if (menuItems[newValue]) {
      navigate(menuItems[newValue].path);
    }
  };

  const menuItems = getMenuItems();
  
  // Ottieni il nome dell'azienda
  const getCompanyDisplay = () => {
    // Se l'utente ha company.name nell'oggetto user, usa quello
    if (user?.company?.name) {
      return user.company.name;
    }
    
    // Altrimenti usa il nome caricato separatamente
    if (companyName) {
      return companyName;
    }
    
    // Se c'è selectedCompany ma non abbiamo ancora il nome, mostra un caricamento
    if (selectedCompany) {
      return "Caricamento...";
    }
    
    // Fallback finale
    return "FreeWheel";
  };

  return (
    <AppBar 
      position="fixed" 
      sx={{ 
        zIndex: (theme) => theme.zIndex.drawer + 1 
      }}
    >
      <Toolbar sx={{ justifyContent: 'flex-start' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
          <IconButton
            edge="start"
            color="inherit"
            sx={{ mr: 2, display: { xs: 'block', lg: 'none' } }}
            onClick={handleMenuOpen}
          >
            <MenuIcon />
          </IconButton>
          
          {/* Logo e Nome Azienda */}
          {user?.freewheelMode ? (
            <Box
              component="img"
              src="https://d2gt4h1eeousrn.cloudfront.net/72654804/header-soNFiV/X4X8Lj2-600x600.webp"
              alt="FreeWheel Logo"
              sx={{ 
                height: 40, 
                mr: 2,
                filter: 'brightness(0) invert(1)' // Rende il logo bianco
              }}
            />
          ) : (
            <Dashboard sx={{ mr: 1 }} />
          )}
          
          {/* Semplifichiamo completamente questa parte */}
          {user?.role === ROLES.SUPERSAIYAN ? (
            <FormControl variant="standard" sx={{ minWidth: 200, mr: 2 }}>
              <Select
                value={selectedCompany || ''}
                onChange={handleCompanyChange}
                displayEmpty
                sx={{ 
                  color: 'white',
                  '&:before': { borderColor: 'white' },
                  '&:after': { borderColor: 'white' },
                  '& .MuiSelect-icon': { color: 'white' }
                }}
              >
                {companies.map((company) => (
                  <MenuItem key={company.id} value={company.id}>
                    {company.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <Typography variant="h6" noWrap sx={{ mr: 2 }}>
              {user?.name || 'Utente'}
            </Typography>
          )}

          {/* Menu per dispositivi desktop e tablet - con scroll automatico */}
          <Box 
            sx={{ 
              display: { xs: 'none', lg: 'flex' }, 
              alignItems: 'center', 
              flex: 1,
              minWidth: 0
            }}
          >
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                flex: 1,
                maxWidth: 'calc(100% - 120px)', // Lascia spazio per il pulsante logout
                mr: 2, // Margine destro per separare dalle altre parti
                '& .MuiTab-root': {
                  color: 'rgba(255, 255, 255, 0.7)',
                  minWidth: 'auto',
                  padding: '6px 12px',
                  '&.Mui-selected': {
                    color: 'white',
                  }
                },
                '& .MuiTabs-indicator': {
                  backgroundColor: 'white',
                },
                '& .MuiTabs-scrollButtons': {
                  color: 'white',
                  '&.Mui-disabled': {
                    opacity: 0.3,
                  }
                }
              }}
            >
              {menuItems.map((item, index) => (
                <Tab
                  key={item.text}
                  icon={item.icon}
                  label={item.text}
                  iconPosition="start"
                  sx={{
                    textTransform: 'none',
                    fontWeight: 'normal',
                    fontSize: '0.875rem'
                  }}
                />
              ))}
            </Tabs>
          </Box>
        </Box>

        {/* Pulsante Logout sempre a destra */}
        <Button
          color="inherit"
          onClick={handleLogout}
          startIcon={<Logout />}
        >
          Logout
        </Button>

        {/* Menu mobile */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          {menuItems.map((item) => (
            <MenuItem 
              key={item.text} 
              component={Link} 
              to={item.path}
              onClick={handleMenuClose}
              sx={{ display: 'flex', alignItems: 'center' }}
            >
              {item.icon}
              <Box sx={{ ml: 1 }}>{item.text}</Box>
            </MenuItem>
          ))}
          <MenuItem 
            onClick={() => {
              handleMenuClose();
              handleLogout();
            }}
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <Logout />
            <Box sx={{ ml: 1 }}>Logout</Box>
          </MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  );
};

export default Navbar;